// Single source of truth for app state that both the bundle and the
// inline <script> blocks in admin/index.php read and write.
//
// Why a window.* container? The inline scripts declare module-scope lets
// like `let terminals = {}` and assign to them freely. If we move any
// function that reads/writes terminals into the bundle, the bundle and
// the inline scripts end up with TWO different `terminals` references —
// assignments in one don't show up in the other.
//
// The fix: declare state once on `window.timestop.state`, and have the
// inline scripts' `let X = ...` lines initialize FROM the container:
//   let terminals = window.timestop.state.terminals;
//
// Reads/writes via the local `terminals` name then mutate the SAME
// object that the bundle sees (because JS objects are reference types).
//
// This container is initialized by a tiny inline <script> in
// admin/index.php that runs synchronously DURING PARSE — before any
// `let X = window.timestop.state.X` line. The bundle's main.ts only
// imports from this shape for type-checking and reads.

import type { CurrentUser, CurrencyConfig, GlobalData, Group, LogEntry, LoginAttemptsPayload, Terminal, TerminalsById, User } from './types';
import type { PushState } from './push';

export interface TimestopState {
  // === Auth ===
  currentUser: CurrentUser | null;
  csrfToken: string | null;

  // === Data ===
  globalData: GlobalData;
  terminals: TerminalsById;     // object keyed by terminal id
  // globalLogs used to be hydrated from /api/data and held in state so
  // the inline scripts' `let globalLogs` binding could share the same
  // object. Phase 12.11 moved logs off /api/data onto a dedicated
  // /api/logs endpoint that the Activity Logs page (navigate(3))
  // lazy-loads; the dashboard no longer carries logs in its payload,
  // so state.globalLogs is gone too. See fetchLogs() in data.ts.
  globalLogs?: LogEntry[];
  groups: Group[];
  users: User[];

  // === Login attempts (Phase 11, admin-only on Users page) ===
  // Most recent /api/login_attempts response. `attempts` is the row
  // log; `per_user` is a username -> {failed_attempts_15m, ...} map
  // matching the badge data attached to each User in state.users.
  loginAttempts: LoginAttemptsPayload;

  // === Modal context ===
  currentTerminal: Terminal | null;

  // === Misc ===
  // Last few fields kept around because the inline scripts reference
  // them. Some are set/used by other layers (Phase 5+, 6+, 7+).
  modalChartInstance: unknown | null;
  hopperRefreshTimer: number | null;

  // === Render bookkeeping ===
  // Set of logKey() values already in the DOM for global logs. Lazily
  // initialised by renderGlobalLogs() so we don't need a separate
  // constructor slot. Mirrors the per-terminal Terminal._renderedLogKeys
  // pattern.
  _globalRenderedLogKeys?: Set<string>;

  // === Per-tab dirty flags (Phase 10.2b) ===
  // Set of tab indices (0..4) whose DOM has in-progress user input
  // and should NOT be re-painted on tab switch. Marked dirty by
  // setPendingSetting / setPendingPrice (Settings tab, idx 2) and
  // cleared by saveSettings (on successful save) and closeModal /
  // openTerminalImpl. switchModalTab(n) skips renderTab(n) if
  // n is in this set.
  modalDirtyTabs?: Set<number>;

  // === Per-ISO currency defaults (Phase 12.16) ===
  // Hydrated by /api/currencies right after login and refreshed by
  // any future "Currencies" admin tab. Looked up by
  // buildFallbackCurrency(iso, state) when a terminal doesn't have
  // the ISO declared in its own cur_recv. Empty object = no DB
  // defaults; the formatter falls back to its hard-coded JS
  // {div:100, fs:'%.2f '+iso} so the BO still works on hosts that
  // haven't run the currencies migration.
  currencyDefaults: Record<string, CurrencyConfig>;

  // === Notify feed (Phase 12.17) ===
  // The current user's pending notify rows, newest-first. The shape
  // is whatever /api/notify projects (see admin/api/index.php):
  //   {id, ts, tsl, mid, cod, par, val, str, mach_name, mach_loc}
  // Populated by fetchNotify() on dashboard load and on the 30s
  // dashboard ticker; mutated in place via replaceArrayInPlace so
  // any code that captured the array reference keeps seeing fresh
  // rows. The dashboard's renderNotify() reads from this and groups
  // by date the same way renderGlobalLogs() does.
  notify: unknown[];

  // === Web Push (Phase 14 / docs/pwa-plan.md §3) ===
  // State for the client-side push subscription flow. The bundle
  // calls ensurePushReady() after login to populate this. Read
  // by the Profile modal's notification section to render the
  // right subscribe/unsubscribe button and the permission hint.
  // Defined here as a forward-ref to ./push (which has the
  // runtime initializer); may be undefined until attachPushState
  // has run for the first time.
  pwa?: PushState;
}

export function createInitialState(initialUser: CurrentUser | null, initialCsrf: string | null): TimestopState {
  return {
    currentUser: initialUser,
    csrfToken: initialCsrf,

    globalData: {},
    terminals: {},
    groups: [],
    users: [],
    loginAttempts: {},

    currentTerminal: null,

    modalChartInstance: null,
    hopperRefreshTimer: null,

    modalDirtyTabs: new Set<number>(),

    currencyDefaults: {},
    notify: [],
  };
}

// Helper to attach the state container to window. Idempotent — does
// nothing if window.timestop already exists (so the inline-script bootstrap
// in index.php wins if it ran first).
export function attachState(state: TimestopState): void {
  interface WindowWithTimestop extends Window {
    timestop?: { state?: TimestopState };
  }
  const w = window as WindowWithTimestop;
  if (!w.timestop) w.timestop = {};
  if (!w.timestop.state) w.timestop.state = state;
}